import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { components } from "@oathera/contract/api";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import { useTimeRange } from "../../state/time-range.tsx";

/**
 * The `GET /api/v1/overview` read for the Overview screen (task 11.4; design 11, 12.2).
 *
 * The response shape is the contract's `Overview` schema — taken straight from the generated
 * `@oathera/contract/api`, never redefined here. The operation is role `viewer` and takes an
 * optional `from`/`to` time window (Unix ms); the screen passes the window resolved from the shared
 * time-range so pausing freezes the snapshot the same way the charts freeze (design 12.2, FR-13).
 *
 * The fetch goes through `lib/api.ts`, so a cold-core 503 surfaces as a `waking` {@link ApiError}
 * and the shell shows "Waking the control plane"; the query is retried per `query-client.ts`.
 */

/** The contract `Overview` response body. */
export type Overview = components["schemas"]["Overview"];

/** The resolved time window the query ran for, so the screen can label the snapshot honestly. */
export interface OverviewWindow {
  readonly fromMs: number;
  readonly toMs: number;
}

export interface UseOverviewResult {
  readonly query: UseQueryResult<Overview, ApiError>;
  /** The window the current query key was built from (what the data describes). */
  readonly window: OverviewWindow;
}

/**
 * Fetch the overview for the current time window. The window is resolved from `useTimeRange`:
 * - Live: a window ending "now" for the selected preset. We resolve it once per render against a
 *   coarse clock (bucketed to 10s) so the query key is stable enough not to refetch on every tick,
 *   while still advancing as the live view moves. TanStack's `staleTime` smooths the rest.
 * - Paused / custom: the frozen window, so the snapshot stops advancing (design 12.2, FR-13 AC2).
 */
export function useOverview(): UseOverviewResult {
  const timeRange = useTimeRange();

  // Resolve the window. In live mode we bucket "now" to a 10s grid so the query key does not change
  // on every render; when frozen, `resolveWindow` returns the exact frozen window regardless of now.
  const nowMs = timeRange.live ? bucket(Date.now(), 10_000) : Date.now();
  const resolved = timeRange.resolveWindow(nowMs);
  const window: OverviewWindow = { fromMs: resolved.fromMs, toMs: resolved.toMs };

  const query = useQuery<Overview, ApiError>({
    queryKey: ["overview", window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<Overview>("/overview", {
        query: { from: window.fromMs, to: window.toMs },
        signal,
      }),
    // A live overview tolerates a little staleness between the 10s window buckets; a frozen window
    // never goes stale because its key does not change.
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // CRITICAL for a seamless live feel: in live mode the window (and therefore the query KEY) shifts
    // every ~10s, which would otherwise make TanStack treat each tick as a brand-new query with no
    // cached data — `query.data` would blink to `undefined` and the screen would fall back through
    // its loading state, unmounting the whole dashboard (a full "reload" flash). `keepPreviousData`
    // holds the last window's data while the next one fetches, so the dashboard stays mounted and the
    // numbers/graphs update in place. (A frozen window has a stable key, so this is a no-op there.)
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/** Floor `value` to the nearest multiple of `size` (used to keep the live query key stable). */
function bucket(value: number, size: number): number {
  return Math.floor(value / size) * size;
}
